/**
 * Réplica local (IndexedDB) de filas de TABLAS para lectura offline.
 *
 * Hermano de `reference-db.ts` (que cubre los catálogos de selectores), pero
 * genérico para cualquier tabla del ERP (cotizaciones, facturas, clientes,
 * productos, ventas, …). El objetivo: que al navegar offline las tablas muestren
 * los registros que ya se cargaron online, en vez del aviso "sin conexión".
 *
 * Enfoque "cache lo que viste": se espejan (upsert por `_id`) las filas que la
 * tabla trae online, acumulando entre páginas/filtros. Offline se sirven con
 * filtrado/orden/paginación client-side (ver table-offline-query.ts).
 *
 * Es solo un espejo de lectura: nunca es la fuente de verdad. El servidor sigue
 * mandando; esto es una caché consultable offline.
 *
 * Un único object store con clave compuesta evita tener que crear stores
 * dinámicos por nombre de tabla (que requerirían bumpear la versión del schema).
 */

import { OFFLINE_CACHE_TTL_MS } from "./offline-ttl";

const DB_NAME = "heskala-tables";
const DB_VERSION = 1;
const STORE = "rows";

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        // keyPath compuesto: "<tabla>::<empresa>::<_id>" (único por fila).
        const store = db.createObjectStore(STORE, { keyPath: "__key" });
        // Índice por (tabla, empresa) para leer/borrar el scope completo.
        store.createIndex("by_scope", ["__tableName", "__companyId"], {
          unique: false,
        });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

type StoredRow = {
  __key: string;
  __tableName: string;
  __companyId: string;
  /** Momento en que se escribió a la caché (epoch ms). Base del TTL. */
  __cachedAt: number;
  _id: string;
  _creationTime?: number;
} & Record<string, unknown>;

function rowKey(tableName: string, companyId: string, id: string): string {
  return `${tableName}::${companyId}::${id}`;
}

/** Holgura sobre el tope antes de podar: no se poda en cada actualización. */
const PRUNE_SLACK = 1.1;

/**
 * Claves a borrar para dejar las `keep` filas más recientes (por
 * `_creationTime`). Vacío mientras no se pase el tope con holgura.
 */
export function keysToPrune(
  rows: ReadonlyArray<{ __key: string; _creationTime?: number }>,
  keep: number,
): string[] {
  if (rows.length <= keep * PRUNE_SLACK) return [];
  return [...rows]
    .sort((a, b) => (b._creationTime ?? 0) - (a._creationTime ?? 0))
    .slice(keep)
    .map((r) => r.__key);
}

/**
 * Upsert de una página y poda en UNA transacción (el costo de IndexedDB está
 * en las transacciones, no en el tamaño). La poda solo lee el alcance cuando
 * el conteo pasa el tope con holgura.
 */
export async function upsertAndPrune(
  tableName: string,
  companyId: string,
  rows: Array<{ _id: string } & Record<string, unknown>>,
  keep: number,
): Promise<void> {
  if (rows.length === 0) return;
  const db = await openDb();
  const now = Date.now();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    for (const row of rows) {
      if (!row?._id) continue;
      store.put({
        ...row,
        __key: rowKey(tableName, companyId, row._id),
        __tableName: tableName,
        __companyId: companyId,
        __cachedAt: now,
        _id: row._id,
      } satisfies StoredRow);
    }
    const scope = IDBKeyRange.only([tableName, companyId]);
    const idx = store.index("by_scope");
    const countReq = idx.count(scope);
    countReq.onsuccess = () => {
      if (countReq.result <= keep * PRUNE_SLACK) return;
      const all = idx.getAll(scope);
      all.onsuccess = () => {
        for (const key of keysToPrune((all.result ?? []) as StoredRow[], keep)) store.delete(key);
      };
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/**
 * Filas cacheadas VIGENTES de una tabla para una empresa (sin scoping interno).
 * Descarta las vencidas según el TTL (defensa de lectura; `purgeAllExpired` las
 * borra del disco).
 */
export async function getRows<T = Record<string, unknown>>(
  tableName: string,
  companyId: string,
): Promise<T[]> {
  const db = await openDb();
  const minCachedAt = Date.now() - OFFLINE_CACHE_TTL_MS;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const idx = tx.objectStore(STORE).index("by_scope");
    const req = idx.getAll(IDBKeyRange.only([tableName, companyId]));
    req.onsuccess = () => {
      const rows = (req.result ?? []) as StoredRow[];
      resolve(
        rows
          .filter((r) => (r.__cachedAt ?? 0) >= minCachedAt)
          .map(({ __key, __tableName, __companyId, __cachedAt, ...rest }) => {
            void __key;
            void __tableName;
            void __companyId;
            void __cachedAt;
            return rest as T;
          }),
      );
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Borra del disco TODAS las filas vencidas (de cualquier tabla/empresa). Se llama
 * una vez al arrancar la app: los datos offline no deben quedar indefinidamente.
 */
export async function purgeAllExpired(): Promise<void> {
  const db = await openDb();
  const minCachedAt = Date.now() - OFFLINE_CACHE_TTL_MS;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const cursorReq = store.openCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) return;
      const row = cursor.value as StoredRow;
      if ((row.__cachedAt ?? 0) < minCachedAt) cursor.delete();
      cursor.continue();
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** Vacía toda la réplica de tablas (p. ej. al cerrar sesión). */
export async function clearAll(): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/**
 * IndexedDB puede no existir (SSR, modo privado). Los helpers lanzan en ese
 * caso; los llamadores envuelven en try/catch y degradan a "solo live".
 */
export function isTableReplicaAvailable(): boolean {
  try {
    return typeof indexedDB !== "undefined";
  } catch {
    return false;
  }
}
